import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {Alert, Button, Card, Col, List, Row, Space, Typography, theme} from 'antd';
import {UserData} from "../generated/user_pb";
import {ReloadOutlined, TrophyFilled, StarFilled, FireOutlined, ThunderboltOutlined} from "@ant-design/icons";
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS} from "../grpcConfig";
import {ScorerClient} from "../generated/score_grpc_web_pb";
import {UserScores as UserScoresProto} from "../generated/score_pb";
import dayjs from "dayjs";
import {useOutbox, usePending} from "@dowty/offline";
import {encodeMessage, outbox, toActivity, useActivities, useOwnScore, userOutbox} from "../offline";
import {Activity} from "../generated/activity_pb";
import {computeStreaks} from "../utils/streaks";
import {ActivitySample, computeRestSuggestion} from "../utils/restSuggestion";
import {EmptyState, ErrorState, LoadingState, PageHeader, StatTile} from "@dowty/ui";
import {isGerman} from '../utils/language';

const {Text} = Typography;

type Score = { key: string; username: string; score: number };

const placeSuffix = (index: number) => {
    if (index === 0) return 'st';
    if (index === 1) return 'nd';
    return 'rd';
};


// A plain object: usePending copies items with a spread, which would strip a protobuf message's methods.
type Entry = { id: number; date?: Date; sport: string; parameterOne: number; parameterTwo: number; parameterOneLabel: string; parameterTwoLabel: string; score?: number };

const toEntry = (activity: Activity): Entry => ({
    id: activity.getId(),
    date: activity.getTsinsert()?.toDate(),
    sport: activity.getSportype()?.getSporttypename() || 'Unknown',
    parameterOne: activity.getParameterone(),
    parameterTwo: activity.getParametertwo(),
    parameterOneLabel: activity.getSportype()?.getParameterone() || (isGerman() ? 'Menge' : 'Amount'),
    parameterTwoLabel: activity.getSportype()?.getParametertwo() || (activity.getParametertwo() ? 'Parameter 2' : ''),
    score: activity.hasScore() ? activity.getScore() : undefined,
});

function summarize(activities: Entry[]) {
    const now = dayjs();
    const weekStart = now.subtract(1, 'week');
    let todayTotal = 0;
    let weekTotal = 0;

    activities.forEach(activity => {
        const activityDate = dayjs(activity.date);
        if (activityDate.isSame(now, 'day')) todayTotal++;
        if (activityDate.isAfter(weekStart)) weekTotal++;
    });
    const streaks = computeStreaks(activities
        .map(activity => activity.date)
        .filter((date): date is Date => date !== undefined));

    const samples: ActivitySample[] = [];
    for (const activity of activities) {
        if (!activity.date) continue;
        samples.push(activity.score === undefined ? {date: activity.date} : {date: activity.date, score: activity.score});
    }
    return {todayCount: todayTotal, weekCount: weekTotal, streaks, restSuggestion: computeRestSuggestion(samples)};
}

function formatRestSuggestion(restSuggestion: ReturnType<typeof computeRestSuggestion>) {
    if (!restSuggestion.suggest) return null;
    const reasons: string[] = [];
    if (restSuggestion.reasons.includes('no-rest')) reasons.push(isGerman() ? `${restSuggestion.consecutiveDays} Tage in Folge ohne Ruhetag` : `${restSuggestion.consecutiveDays} days in a row without a rest day`);
    if (restSuggestion.reasons.includes('score-drop')) reasons.push(isGerman() ? 'deine letzten Punkte liegen unter deinem üblichen Wert' : 'your recent scores are below your usual');
    return isGerman()
        ? `Gut gemacht – ${reasons.join(' und ')}. Ein Ruhetag erhält deine Serie (bis zu zwei Ruhetage zwischen Einheiten sind möglich) und hilft dir, gestärkt zurückzukommen.`
        : `Nice work - ${reasons.join(' and ')}. Taking a rest day now still keeps your streak alive (up to two rest days between sessions are fine) and helps you come back stronger.`;
}

function recentActivitiesContent(loading: boolean, error: boolean, activities: Entry[], onReload: () => void, onAddActivityClick: () => void) {
    if (loading) return <LoadingState variant="list" label={isGerman() ? 'Aktivitäten werden geladen' : 'Loading activities'} />;
    if (error) return <ErrorState message={isGerman() ? 'Aktivitäten konnten nicht geladen werden.' : 'Could not load activities.'} retry={onReload} retryLabel={isGerman() ? 'Erneut versuchen' : 'Retry'} />;
    if (activities.length === 0) return <EmptyState title={isGerman() ? 'Noch keine Aktivitäten' : 'No activities yet'} description={isGerman() ? 'Deine letzten Einheiten erscheinen hier.' : 'Your latest sessions will appear here.'} action={<Button type="primary" onClick={onAddActivityClick}>{isGerman() ? 'Aktivität eintragen' : 'Log activity'}</Button>} />;
    return <List dataSource={activities} renderItem={activity => <List.Item key={activity.id}>
        <Space direction="vertical" size={0}>
            <Text strong>{activity.sport}</Text>
            <Text type="secondary">{activity.date && dayjs(activity.date).format(isGerman() ? 'DD.MM.YYYY' : 'MMM D, YYYY')} · {activity.parameterOneLabel}: {activity.parameterOne}{activity.parameterTwoLabel ? ` · ${activity.parameterTwoLabel}: ${activity.parameterTwo}` : ''}</Text>
        </Space>
    </List.Item>} />;
}

const Leaderboard: React.FC<{scores: Score[]; error: boolean; onReload: () => void}> = ({scores, error, onReload}) => {
    const {token} = theme.useToken();
    const medalIcons = [TrophyFilled, StarFilled, StarFilled];
    const medalColors = [token.colorWarning, token.colorTextSecondary, token.colorWarning];
    return <Card title={<Space><TrophyFilled />Leaderboard this week</Space>}>
        {error && <ErrorState
            message={isGerman() ? 'Bestenliste konnte nicht geladen werden.' : 'Could not load leaderboard.'}
            retry={onReload}
            retryLabel={isGerman() ? 'Erneut versuchen' : 'Retry'}
        />}
        <List
            dataSource={scores}
            locale={{emptyText: <EmptyState title="No scores yet" description="Log an activity to get on the leaderboard." />}}
            renderItem={(player, index) => {
                const MedalIcon = medalIcons[index] ?? null;
                return <List.Item key={player.key}>
                    <Space>
                        {MedalIcon ? <MedalIcon aria-label={`${index + 1}${placeSuffix(index)} place`} style={{color: medalColors[index]}} /> : <Text type="secondary">{index + 1}</Text>}
                        <Text strong={index < 3}>{player.username}</Text>
                    </Space>
                    <Text strong>{player.score}</Text>
                </List.Item>;
            }}
        />
    </Card>;
};

type DashboardProps = { onAddActivityClick: () => void };

const Dashboard: React.FC<DashboardProps> = ({onAddActivityClick}) => {
    const [leaderboard, setLeaderboard] = useState<Score[]>([]);
    const [leaderboardError, setLeaderboardError] = useState(false);
    const scorerClient = useMemo(() => new ScorerClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS), []);
    const activitiesRead = useActivities();
    const serverActivities = useMemo(() => activitiesRead.data?.getActivitiesList().map(toEntry) ?? [], [activitiesRead.data]);
    // Queued inserts count like saved ones; a queued update is hidden because its saved row is already listed.
    const activities = usePending(serverActivities, userOutbox, {
        toOptimistic: payload => toEntry(toActivity(payload)),
        matches: (item, entry) => entry.payload.id === item.id,
    });
    const {todayCount, weekCount, streaks, restSuggestion} = summarize(activities);
    const recentActivities = useMemo(() => activities.filter(activity => activity.date).sort((a, b) => b.date!.getTime() - a.date!.getTime()).slice(0, 5), [activities]);
    const isLoading = activitiesRead.loading;
    const {pendingCount} = useOutbox(userOutbox);
    const ownScore = useOwnScore().data?.getUsersscoresList().reduce((sum, day) => sum + day.getScore(), 0);

    const reloadPage = useCallback(() => {
        const cachedScores = localStorage.getItem('userScores');
        if (cachedScores !== null) {
            const userScores = UserScoresProto.deserializeBinary(Uint8Array.from(atob(cachedScores), c => c.codePointAt(0)!));
            loadLeaderboard(userScores);
        }

        const storedUser = localStorage.getItem('user');
        if (storedUser == null) return;
        const user = UserData.deserializeBinary(Uint8Array.from(atob(storedUser), c => c.codePointAt(0)!));
        const request = new UserScoresProto();
        request.setUser(user);
        request.setDaylength(7);
        scorerClient.getTopUser(request, undefined, (err, response) => {
            if (err) {
                setLeaderboardError(true);
                localStorage.removeItem('userScores');
            }
            if (response) {
                setLeaderboardError(false);
                localStorage.setItem('userScores', encodeMessage(response));
                loadLeaderboard(response);
            }
        });
    }, [scorerClient]);

    useEffect(() => { reloadPage(); }, [reloadPage]);
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        const unsubscribe = outbox.subscribe(event => {
            if (event.type !== 'confirmed') return;
            if (timeout !== undefined) clearTimeout(timeout);
            timeout = setTimeout(reloadPage, 300);
        });
        return () => {
            if (timeout !== undefined) clearTimeout(timeout);
            unsubscribe();
        };
    }, [reloadPage]);

    function loadLeaderboard(response: UserScoresProto) {
        setLeaderboard(response.getUsersscoresList().map((item, index) => ({
            key: String(index + 1),
            username: item.getUser()?.getUsername() ?? '',
            score: item.getScore(),
        })));
    }

    const restSuggestionMessage = useMemo(() => formatRestSuggestion(restSuggestion), [restSuggestion]);

    return (
        <div className="sf-dashboard-page">
            <PageHeader title="Your Activity Summary" extra={<Space>
                <Button type="default" onClick={onAddActivityClick}>Log activity</Button>
                <Button aria-label="Reload dashboard" icon={<ReloadOutlined />} onClick={() => { reloadPage(); activitiesRead.reload(); }} />
            </Space>} />

            {isLoading ? <LoadingState variant="stats" label="Loading your stats" /> : <Row gutter={[{xs: 12, md: 16}, {xs: 12, md: 16}]}>
                <Col xs={12} lg={6}><StatTile label="Sessions today" value={todayCount} size="xl" /></Col>
                <Col xs={12} lg={6}><StatTile label="Current streak" value={streaks.current} unit={streaks.current === 1 ? 'day' : 'days'} size="xl" /></Col>
                <Col xs={12} lg={6}><StatTile label="Sessions this week" value={weekCount} size="xl" /></Col>
                {(ownScore !== undefined || pendingCount > 0) && <Col xs={12} lg={6}>
                    <StatTile label="Your score this week" value={ownScore ?? 0} unit={pendingCount > 0 ? `+${pendingCount} pending` : undefined} size="xl" />
                </Col>}
            </Row>}

            {restSuggestionMessage && <Alert className="sf-rest-suggestion" type="info" showIcon message={isGerman() ? 'Erholungstag einplanen' : 'Consider a rest day'} description={restSuggestionMessage} />}

            <Leaderboard scores={leaderboard} error={leaderboardError} onReload={reloadPage} />

            <Card title={<Space><FireOutlined />{isGerman() ? 'Letzte Aktivitäten' : 'Recent activity'}</Space>} extra={<Link to="/history">{isGerman() ? 'Alle anzeigen' : 'Show all'}</Link>}>
                {recentActivitiesContent(isLoading, Boolean(activitiesRead.error), recentActivities, activitiesRead.reload, onAddActivityClick)}
                {streaks.longest > 0 && <Text type="secondary"><ThunderboltOutlined /> {isGerman() ? `Längste Serie: ${streaks.longest} Tage` : `Longest streak: ${streaks.longest} days`}</Text>}
            </Card>
        </div>
    );
};

export default Dashboard;
